import React, { useState, useEffect } from 'react';
import { portfolioConfig } from '../config/portfolio';
import { Icon } from './Icons';

const Hero = () => {
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [typeSpeed, setTypeSpeed] = useState(150);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const roles = portfolioConfig.personal.roles;
    const currentRole = roles[currentRoleIndex];

    const handleType = () => {
      if (isDeleting) {
        setDisplayText(currentRole.substring(0, displayText.length - 1));
        setTypeSpeed(50);
      } else {
        setDisplayText(currentRole.substring(0, displayText.length + 1));
        setTypeSpeed(150);
      }

      if (!isDeleting && displayText === currentRole) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && displayText === '') {
        setIsDeleting(false);
        setCurrentRoleIndex((prev) => (prev + 1) % roles.length);
      }
    };

    const timer = setTimeout(handleType, typeSpeed);
    return () => clearTimeout(timer);
  }, [displayText, isDeleting, currentRoleIndex, typeSpeed]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden px-6 md:px-12">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-cyan-500/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-48 h-48 bg-blue-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full blur-2xl animate-spin-slow"></div>
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(6, 182, 212, 0.3) 1px, transparent 0)',
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Image */}
          <div
            className={`flex justify-center lg:justify-start mt-8 lg:mt-0 transition-all duration-1000
            ${isMounted ? 'opacity-100 translate-y-0 lg:translate-x-0' : 'opacity-0 -translate-y-8 lg:-translate-x-8'}`}
          >
            <div className="relative animate-bounce-gentle">
              <div className="w-80 h-96 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-cyan-400/30 rotate-3 hover:rotate-0 relative">
                <img
                  src={portfolioConfig.personal.image}
                  alt={portfolioConfig.personal.name}
                  className={`w-full h-full object-cover transition-opacity duration-700 ${
                    isImageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setIsImageLoaded(true)}
                  loading="eager"
                  fetchpriority="high"
                />
                {!isImageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900/70">
                    <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-400/20 via-transparent to-transparent"></div>
              </div>

              {/* Resume Download Button */}
              <div className="absolute -bottom-4 -right-4">
                <div className="relative inline-flex items-center justify-center">
                  <div
                    className="absolute aspect-square w-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full opacity-5 animate-[ping_4s_ease-out_infinite]"
                  />
                  <a
                    href={portfolioConfig.personal.resumeUrl}
                    download="SSK_Resume.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-black font-medium rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-cyan-500/30 ring-2 ring-cyan-400/30 relative z-10"
                    style={{ transformOrigin: 'center' }}
                  >
                    <Icon name="download" className="group-hover:animate-pulse" />
                    <span>Resume</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div
            className={`text-center lg:text-left space-y-6 transition-all duration-1000
            ${isMounted ? 'opacity-100 translate-y-0 lg:translate-x-0' : 'opacity-0 translate-y-8 lg:translate-x-8'}`}
          >
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                Hi, I'm{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 whitespace-nowrap">
                  {portfolioConfig.personal.name}
                </span>
              </h1>

              <div className="text-xl md:text-2xl text-gray-300 min-h-[2em]">
                I'm a{' '}
                <span className="text-cyan-400 font-semibold">
                  {displayText}
                  <span className="animate-pulse">|</span>
                </span>
              </div>
            </div>

            <p className="text-lg text-gray-300 leading-relaxed max-w-2xl">
              {portfolioConfig.personal.summary}
            </p>

            {/* Social Buttons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3">
              <a href={portfolioConfig.contact.linkedin} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center w-12 h-12 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-cyan-400 rounded-lg transition-all duration-200 transform hover:scale-105" title="LinkedIn"><Icon name="linkedin" /></a>
              <a href={portfolioConfig.contact.github} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center w-12 h-12 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-cyan-400 rounded-lg transition-all duration-200 transform hover:scale-105" title="GitHub"><Icon name="github" /></a>
              <a href={portfolioConfig.contact.codeforces} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center w-12 h-12 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-cyan-400 rounded-lg transition-all duration-200 transform hover:scale-105" title="Codeforces"><Icon name="cf" /></a>
              <a href={portfolioConfig.contact.codechef} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center w-12 h-12 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-cyan-400 rounded-lg transition-all duration-200 transform hover:scale-105" title="CodeChef"><Icon name="chef" /></a>
              <a href={portfolioConfig.contact.leetcode} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center w-12 h-12 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-cyan-400 rounded-lg transition-all duration-200 transform hover:scale-105" title="LeetCode"><Icon name="leetcode" /></a>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center lg:justify-start">
              <button
                onClick={() => scrollToSection('projects')}
                className="group w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-black font-medium rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-cyan-500/30"
              >
                <span className="flex items-center justify-center gap-2">
                  <Icon name="briefcase" className="group-hover:animate-pulse" />
                  View My Work
                </span>
              </button>

              <button
                onClick={() => scrollToSection('contact')}
                className="group w-full sm:w-auto px-8 py-3 border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black font-medium rounded-full transition-all duration-200 transform hover:scale-105"
              >
                <span className="flex items-center justify-center gap-2">
                  <Icon name="message-circle" className="group-hover:animate-pulse" />
                  Let's Connect
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
